/**
 * Source hooks for API interactions
 *
 * Provides hooks for:
 * - Listing sources for a lesson (with polling for processing status)
 * - Uploading file/link/text sources
 * - Deleting sources
 * - Regenerating lesson with updated sources
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { track } from '@/config/observability';
import {
  lessonApi,
  sourceApi,
  versionApi,
  type LessonMetadata,
  type SourceItem,
  type SourceListResponse,
  type SourceUploadResponse,
  type SourceChunksResponse,
  type SourceFileResponse,
  type RegenerateResponse,
  type StagedStateResponse,
  type VersionListResponse,
  type VersionSummary,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import { versionKeys } from '@/hooks/lesson/useVersions';
import { skippedStepsNotice } from '@/lib/lesson-error';
import { notify } from '@/lib/notify';
import { handleStepConflict, isStepConflict } from '@/lib/stepConflict';

function bumpMajor(version: string): string {
  const major = parseInt(version.split('.')[0] ?? '1', 10);
  return `${Number.isFinite(major) ? major + 1 : 2}.0`;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const sourceKeys = {
  all: ['sources'] as const,
  list: (lessonId: string) => [...sourceKeys.all, 'list', lessonId] as const,
  detail: (sourceId: string) =>
    [...sourceKeys.all, 'detail', sourceId] as const,
  chunks: (sourceId: string) =>
    [...sourceKeys.all, 'chunks', sourceId] as const,
  file: (sourceId: string) => [...sourceKeys.all, 'file', sourceId] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook for listing sources for a lesson.
 * Polls every 5 seconds when any source is still processing.
 */
export function useSourcesList(lessonId: string | undefined) {
  const query = useQuery({
    queryKey: sourceKeys.list(lessonId || ''),
    queryFn: () => sourceApi.listSources(lessonId!),
    enabled: !!lessonId,
    staleTime: 10 * 1000,
    refetchInterval: query => {
      const data = query.state.data as SourceListResponse | undefined;
      if (!data) return false;
      const hasProcessing = data.sources.some(
        s => s.status === 'pending' || s.status === 'processing'
      );
      return hasProcessing ? 5000 : false;
    },
  });

  return {
    sources: query.data?.sources ?? [],
    total: query.data?.total_count ?? 0,
    hasPending: query.data?.has_pending ?? false,
    /** Sources the lesson can still add (null until loaded). */
    slotsLeft: query.data?.slots_left ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Re-read the Sources list when the pipeline finishes reading sources. The
 * list polls only while a listed source is processing, so a first answer
 * that came before the lesson's sources were linked (an empty list) was
 * never refreshed until a reload.
 */
export function useRefreshSourcesWhenRead(
  lessonId: string | undefined,
  sourcesState: string | undefined
) {
  const queryClient = useQueryClient();
  const prev = useRef(sourcesState);
  useEffect(() => {
    const was = prev.current;
    prev.current = sourcesState;
    if (lessonId && sourcesState === 'completed' && was !== 'completed') {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
    }
  }, [lessonId, sourcesState, queryClient]);
}

/**
 * The backend pushes every panel add into `staged_source_ids`
 * (`source/creation.py`), so the lesson metadata is stale the moment an add
 * lands. It has no poll and a 5-minute `staleTime`, so without this the new
 * row reads as unstaged and `StagedActionBar` shows no diff until a reload.
 */
function invalidateAfterAdd(
  queryClient: ReturnType<typeof useQueryClient>,
  lessonId: string,
  added: SourceUploadResponse
) {
  trackSourceAdded(added);
  queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
  queryClient.invalidateQueries({
    queryKey: queryKeys.lessons.metadata(lessonId),
  });
}

function trackSourceAdded(source: SourceUploadResponse) {
  track('source_added', {
    where: 'lesson',
    type: source.type,
    link_type: source.link_type,
    mime_type: source.mime_type,
    page_count: source.page_count,
  });
}

/**
 * Hook for uploading a file source.
 */
export function useUploadFileSource(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<SourceUploadResponse, Error, File>({
    mutationFn: file => sourceApi.uploadFile(lessonId, file),
    onSuccess: source => invalidateAfterAdd(queryClient, lessonId, source),
  });
}

/**
 * Hook for adding a link source.
 */
export function useAddLinkSource(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    SourceUploadResponse,
    Error,
    { url: string; name?: string }
  >({
    mutationFn: data => sourceApi.addLink(lessonId, data),
    onSuccess: source => invalidateAfterAdd(queryClient, lessonId, source),
  });
}

/**
 * Hook for adding a text source.
 */
export function useAddTextSource(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    SourceUploadResponse,
    Error,
    { text: string; name?: string; description?: string }
  >({
    mutationFn: data => sourceApi.addText(lessonId, data),
    onSuccess: source => invalidateAfterAdd(queryClient, lessonId, source),
  });
}

/**
 * Hook for applying a scope selection to an AWAITING_SCOPE source. Ingests only
 * the chosen outline sections, then the lesson generation resumes. Invalidates
 * the sources list + lesson metadata + pipeline status so the UI moves from the
 * scope prompt into the normal generating view.
 */
export function useScopeSource(lessonId: string, sourceId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    SourceUploadResponse,
    Error,
    {
      section_ids?: string[];
      page_ranges?: { start: number; end: number }[];
      sheet_rows?: Record<string, { start: number; end: number }>;
    }
  >({
    mutationFn: selection =>
      sourceApi.scopeSource(lessonId, sourceId, selection),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.metadata(lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.pipelineStatus(lessonId),
      });
    },
  });
}

/**
 * Hook for deleting a source.
 */
export function useDeleteSource(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: sourceId => sourceApi.deleteSource(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
    },
  });
}

/**
 * Hook for re-ingesting a single failed source. Hits the per-source retry
 * endpoint (which also re-opens the context latch) and invalidates the list
 * so the row flips Failed → Queued without waiting for a poll (the list only
 * polls while something is already processing).
 */
export function useRetrySource(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string, { prev?: SourceListResponse }>({
    mutationFn: sourceId => lessonApi.retrySource(lessonId, sourceId),
    // A 409 ("doesn't need a retry") won't change on a second attempt.
    retry: (failureCount, err) => !isStepConflict(err) && failureCount < 1,
    onMutate: async sourceId => {
      // Flip the retried source to "processing" instantly so its status pill
      // updates on click instead of waiting for the 5s source-list poll.
      const key = sourceKeys.list(lessonId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<SourceListResponse>(key);
      if (prev) {
        queryClient.setQueryData<SourceListResponse>(key, {
          ...prev,
          sources: prev.sources.map(s =>
            s.source_id === sourceId
              ? { ...s, status: 'processing', error: null }
              : s
          ),
        });
      }
      return { prev };
    },
    onError: (err, _sourceId, ctx) => {
      // Retry POST failed - restore the prior list so we don't show a fake
      // "processing".
      if (ctx?.prev)
        queryClient.setQueryData(sourceKeys.list(lessonId), ctx.prev);
      // 409 = the source didn't need a retry (already ready / in flight):
      // refresh the list to show its real state, calmly.
      handleStepConflict(err, queryClient, lessonId, [
        sourceKeys.list(lessonId),
      ]);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
    },
  });
}

/**
 * Hook for renaming a source's display name.
 *
 * Backend cascades the new name into existing LessonText.cited_bytes
 * snapshots - invalidating the lesson-text cache here picks up those
 * fresh source_name values on the next refetch.
 */
export function useRenameSource(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<SourceItem, Error, { sourceId: string; name: string }>({
    mutationFn: ({ sourceId, name }) => sourceApi.renameSource(sourceId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.text(lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.mindmap(lessonId),
      });
    },
  });
}

/**
 * Hook for getting source details.
 */
export function useSourceDetail(sourceId: string | null) {
  return useQuery<SourceItem>({
    queryKey: sourceKeys.detail(sourceId || ''),
    queryFn: () => sourceApi.getSource(sourceId!),
    enabled: !!sourceId,
  });
}

/**
 * Hook for getting presigned file URL.
 */
export function useSourceFileUrl(sourceId: string | null) {
  return useQuery<SourceFileResponse>({
    queryKey: sourceKeys.file(sourceId || ''),
    queryFn: () => sourceApi.getFileUrl(sourceId!),
    enabled: !!sourceId,
    staleTime: 4 * 60 * 1000, // Presigned URLs are valid ~5 min, refresh at 4
  });
}

/**
 * Hook for fetching pasted-text source content. Only valid for sources
 * created via "paste text" - file/link types will 400 from the backend.
 */
export function useSourceRawText(sourceId: string | null) {
  return useQuery<{ text: string; char_count: number }>({
    queryKey: [...sourceKeys.all, 'rawtext', sourceId || ''],
    queryFn: () => sourceApi.getSourceText(sourceId!),
    enabled: !!sourceId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for getting source chunks (paginated).
 */
export function useSourceChunks(
  sourceId: string | null,
  params?: { page?: number; limit?: number }
) {
  return useQuery<SourceChunksResponse>({
    queryKey: [...sourceKeys.chunks(sourceId || ''), params],
    queryFn: () => sourceApi.getChunks(sourceId!, params),
    enabled: !!sourceId,
  });
}

/**
 * Hook for triggering lesson regeneration.
 *
 * On mutate, optimistically injects a placeholder version into the
 * cached `versionKeys.list` so the version selector can render
 * "v{next} generating…" with a spinner while the backend creates the
 * real LessonVersion record. On settle, all version/source/pipeline
 * caches are invalidated so real data replaces the placeholder.
 */
export function useRegenerateLesson(lessonId: string) {
  const queryClient = useQueryClient();
  const listKey = versionKeys.list(lessonId);

  return useMutation<
    RegenerateResponse,
    Error,
    void,
    { previous: VersionListResponse | undefined }
  >({
    mutationFn: () => sourceApi.regenerateLesson(lessonId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<VersionListResponse>(listKey);

      if (previous) {
        const nextVersion = bumpMajor(previous.current_version);
        const alreadyHas = previous.versions.some(
          v => v.version === nextVersion
        );
        if (!alreadyHas) {
          const placeholder: VersionSummary & { _generating?: boolean } = {
            version: nextVersion,
            is_current: false,
            trigger: 'source_added',
            trigger_details: 'Generating…',
            source_ids: [],
            user_prompt: null,
            title: null,
            description: null,
            tags: [],
            content_status: {
              context: 'processing',
              text: 'pending',
              mindmap: 'pending',
              video: 'pending',
              quiz: 'pending',
              flashcards: 'pending',
              glossary: 'pending',
              extras: 'pending',
              concepts: 'pending',
            },
            created_at: new Date().toISOString(),
            _generating: true,
          };
          queryClient.setQueryData<VersionListResponse>(listKey, {
            ...previous,
            versions: [placeholder, ...previous.versions],
          });
        }
      }

      return { previous };
    },
    onSuccess: response => {
      track('lesson_regenerated', { lesson_id: lessonId });
      const skipped = skippedStepsNotice(response.skipped_steps);
      if (skipped) notify.info(skipped);
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(listKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.invalidateQueries({ queryKey: sourceKeys.list(lessonId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.metadata(lessonId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.pipelineStatus(lessonId),
      });
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
}

/**
 * Toggle a single source's checkbox in the staged set. Reads current
 * staged ids from the lesson metadata cache, sends the new full set to
 * the backend (PUT semantics), and applies an optimistic update so the
 * checkbox flips instantly. On error or invalidation, the server value
 * is the source of truth.
 */
export function useToggleStagedSource(lessonId: string) {
  const queryClient = useQueryClient();
  const metaKey = queryKeys.lessons.metadata(lessonId);

  // When `staged_source_ids` is null/undefined the user hasn't edited yet -
  // the UI treats the *current version's* source_ids as the visible baseline,
  // so the toggle mutation must use the same baseline. Otherwise the first
  // toggle would send `[]` (or `[sourceId]`) and wipe every other source the
  // user thought was already included.
  const resolveBaseline = (meta: LessonMetadata | undefined): string[] => {
    if (meta?.staged_source_ids != null) return meta.staged_source_ids;
    const versions = queryClient.getQueryData<VersionListResponse>(
      versionKeys.list(lessonId)
    );
    const currentVersion = versions?.versions.find(v => v.is_current);
    return currentVersion?.source_ids ?? [];
  };

  return useMutation<
    StagedStateResponse,
    Error,
    { sourceId: string; checked: boolean },
    { previous: LessonMetadata | undefined }
  >({
    mutationFn: ({ sourceId, checked }) => {
      const current = resolveBaseline(
        queryClient.getQueryData<LessonMetadata>(metaKey)
      );
      const next = checked
        ? Array.from(new Set([...current, sourceId]))
        : current.filter(id => id !== sourceId);
      return versionApi.putStagedSources(lessonId, next);
    },
    onMutate: async ({ sourceId, checked }) => {
      await queryClient.cancelQueries({ queryKey: metaKey });
      const previous = queryClient.getQueryData<LessonMetadata>(metaKey);
      if (previous) {
        const cur = resolveBaseline(previous);
        const next = checked
          ? Array.from(new Set([...cur, sourceId]))
          : cur.filter(id => id !== sourceId);
        queryClient.setQueryData<LessonMetadata>(metaKey, {
          ...previous,
          staged_source_ids: next,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(metaKey, ctx.previous);
    },
    // Write the server's authoritative response straight into the cache so
    // a concurrent metadata poll (LessonLearningPage refetches every 3s) can
    // not clobber the user's selection with a stale read between the PUT
    // returning and the next poll observing the new state.
    onSuccess: data => {
      queryClient.setQueryData<LessonMetadata | undefined>(metaKey, prev =>
        prev
          ? {
              ...prev,
              staged_source_ids: data.staged_source_ids,
              staged_prompt: data.staged_prompt,
            }
          : prev
      );
    },
  });
}

/**
 * Discard the staged source selection + draft prompt - reverts back to
 * the current LessonVersion's source set.
 */
export function useDiscardStaged(lessonId: string) {
  const queryClient = useQueryClient();
  const metaKey = queryKeys.lessons.metadata(lessonId);

  return useMutation<StagedStateResponse, Error, void>({
    mutationFn: () => versionApi.discardStaged(lessonId),
    // Force `staged_source_ids` to null after discard regardless of what the
    // backend returns. The backend sometimes returns `[]` for the discarded
    // state, which the UI would otherwise interpret as "everything unchecked"
    // and show a stale "N removed" diff in the StagedActionBar.
    onSuccess: () => {
      queryClient.setQueryData<LessonMetadata | undefined>(metaKey, prev =>
        prev
          ? {
              ...prev,
              staged_source_ids: null,
              staged_prompt: null,
            }
          : prev
      );
    },
  });
}

/**
 * Save a draft prompt edit. The prompt is persisted server-side as
 * `staged_prompt` but is NOT applied until the user clicks "Create new
 * version" - at which point the same `useRegenerateLesson` consumes it.
 */
export function useSaveDraftPrompt(lessonId: string) {
  const queryClient = useQueryClient();
  const metaKey = queryKeys.lessons.metadata(lessonId);

  return useMutation<
    { lesson_id: string; staged_prompt: string | null },
    Error,
    string
  >({
    mutationFn: (prompt: string) => lessonApi.editPrompt(lessonId, prompt),
    onSuccess: data => {
      queryClient.setQueryData<LessonMetadata | undefined>(metaKey, prev =>
        prev ? { ...prev, staged_prompt: data.staged_prompt } : prev
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: metaKey });
    },
  });
}
